import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { createAction, PayloadAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { Optional } from 'packages/types'
import { actions as userActions, UserData, selectors as userSelectors } from 'builder/modules/user'
import { JoyRidesJobSearchTrackingUtils } from 'builder/components/FindJob/JoyRidesJobSearch/JoyRidesJobSearchTrackingUtils'
import { BriefResume, actions as panelActions, UIStatesConfigStore } from 'builder/modules/panel'
import {
  createAutoTailoringUrlParams,
  CreateJobPostingResponse,
  AutoTailoringMode,
  FetchScoreResponse,
  AutoTailoringFromPublicJobBoardStep,
} from 'builder/modules/resumeEditor'
import { Job as JobFromJS } from 'builder/components/FindJob/JobSearchResult/types'
import { stripHtml } from 'builder/utils/stripHTML'
import ErrorLogger from 'builder/services/ErrorLogger'
import { Store } from '../store'
import { ColumnName, Job as JobFromJT } from '../jobTracking'
import { createDocumentSaga } from '../panel/panelSagas'
import { DocumentTypes } from '../constants'
import { createCardJTSaga } from '../jobTracking/jobTrackingSagas'
import { navigate } from '../navigate'
import { baseClient } from '../apiClient'
import { AutoApplyBubbles, JobSearchNotificationType } from './types'
import { actions } from './jobSearchModule'

export const sagaActions = {
  fetchJobSearchBubbleFeatureFlag: createAction('builder/FETCH_JOB_SEARCH_BUBBLE_FEATURE_FLAG'),
  disableAutoApplyBubble: createAction('builder/DISABLE_AUTO_APPLY_BUBBLES_JS'),
  startAutoApplyBubble: createAction<{ enableFirstPage: boolean }>(
    'builder/START_AUTO_APPLY_BUBBLES_JS',
  ),
  handleNotificationAddedJobCard: createAction<JobFromJT>(
    'builder/HANDLE_NOTIFICATION_ADDED_JOB_CARD',
  ),
  handleNotificationRemovedJobCard: createAction('builder/HANDLE_NOTIFICATION_REMOVED_JOB_CARD'),
  autoTailoringFromJS: createAction<{
    cardId?: number
    resumeId?: number
    job: JobFromJS
  }>('builder/AUTO_TAILORING_FROM_JS'),
  manualTailoringFromJS: createAction<{
    cardId?: number
    resumeId?: number
    job: JobFromJS
  }>('builder/MANUAL_TAILORING_FROM_JS'),
}

function* fetchJobSearchBubbleFeatureFlag() {
  const userId: number | undefined = yield select((state: Store) => state?.user?.data?.id)
  const uiStates: UIStatesConfigStore | undefined = yield select(
    (state: Store) => state?.user?.data?.uiStates,
  )

  if (!userId) {
    return
  }

  const localStorage = JoyRidesJobSearchTrackingUtils.readLocalStorage(userId)
  const running =
    window.location.pathname === '/app/job-search' ? false : Boolean(localStorage.running)

  if (localStorage.version !== 'v2') {
    yield put(
      actions.setAutoApplyBubble({
        userStartedAutoApplyBubbles: localStorage.enable,
        running,
        showAutoApplyGuideButton:
          localStorage.showGuideButton || uiStates?.showAutoApplyGuideButton || false,
        stepIndex: localStorage.stepIndex || 0,
        showFirstPage: false,
        initialized: true,
        selectedJob: undefined,
      }),
    )
  } else {
    yield put(
      actions.setAutoApplyBubble({
        userStartedAutoApplyBubbles: uiStates?.userStartedAutoApplyBubbles || false,
        running,
        showAutoApplyGuideButton: uiStates?.showAutoApplyGuideButton || false,
        stepIndex: localStorage.stepIndex || 0,
        showFirstPage: localStorage.showFirstPage,
        initialized: true,
        selectedJob: undefined,
      }),
    )
  }
}

function* setAutoApplyBubbleSaga(action: PayloadAction<Partial<AutoApplyBubbles>>) {
  yield put(
    userActions.setUIUserState({
      userStartedAutoApplyBubbles: action.payload.userStartedAutoApplyBubbles,
      showAutoApplyGuideButton: action.payload.showAutoApplyGuideButton,
    }),
  )
  yield put(
    panelActions.setUIStatesConfigStatus({
      user_started_auto_apply_bubbles: action.payload.userStartedAutoApplyBubbles,
      show_auto_apply_guide_button: action.payload.showAutoApplyGuideButton,
    }),
  )
}

function* disableAutoApplyBubbleSaga() {
  yield put(
    actions.setAutoApplyBubble({
      userStartedAutoApplyBubbles: false,
      showAutoApplyGuideButton: true,
      running: false,
      stepIndex: 0,
      showFirstPage: false,
      initialized: true,
    }),
  )
}

function* startAutoApplyBubbleSaga(action: PayloadAction<{ enableFirstPage: boolean }>) {
  yield put(
    actions.setAutoApplyBubble({
      userStartedAutoApplyBubbles: true,
      running: false,
      showAutoApplyGuideButton: false,
      stepIndex: 0,
      showFirstPage: action.payload.enableFirstPage,
      initialized: true,
    }),
  )
}

function* handleNotificationAddedJobCard(action: PayloadAction<JobFromJT>) {
  // Only show notification for job-search
  if (!window.location.pathname.includes('job-search')) {
    return
  }
  const card = action.payload
  yield put(actions.setNotification(undefined))
  switch (card.status) {
    case ColumnName.auto_apply:
      yield put(actions.setNotification(JobSearchNotificationType.auto_apply_added))
      break
    case ColumnName.wishlist:
      yield put(actions.setNotification(JobSearchNotificationType.job_card_added))
      break
  }
}

function* handleNotificationRemovedJobCard() {
  yield put(actions.setNotification(undefined))
}

function* createCardIfMissed(cardId: Optional<number>, job: JobFromJS) {
  if (!cardId) {
    const card: JobFromJT = yield call(createCardJTSaga, {
      payload: {
        status: ColumnName.wishlist,
        title: job.job_title,
        company: job.company,
        link: job.url,
        description: stripHtml(job.description),
        open: false,
        external_slug_id: job.external_slug_id,
        location: job.location,
        logo: job.company_logo,
        is_easy_apply: job.is_easy_apply,
      },
    })
    cardId = card.id
  }
  return cardId
}

function* createResumeIfMissed(resumeId: Optional<number>) {
  if (!resumeId) {
    const resume: BriefResume = yield call(createDocumentSaga, {
      payload: {
        type: DocumentTypes.resume,
        isResumeDistributionCreateAction: false,
        disableRedirect: true,
      },
    })
    resumeId = resume?.id
  }
  return resumeId
}

function* handleAutoTailoringFromJS({
  payload,
}: ReturnType<typeof sagaActions.autoTailoringFromJS>) {
  try {
    let { resumeId, job, cardId } = payload

    resumeId = yield call(createResumeIfMissed, resumeId)

    if (!resumeId) {
      yield put(actions.setModal())
      return
    }

    cardId = yield call(createCardIfMissed, cardId, job)

    const {
      data: { jobPosting },
    }: AxiosResponse<CreateJobPostingResponse> = yield call(
      baseClient.post,
      '/tailored_resumes/create_job_posting',
      {
        job_posting: {
          description: job.description,
          job_title: job.job_title,
          company_name: job.company,
        },
      },
    )

    /**
     * It's important to call this api to update keywords array in job posting
     * before moving forward
     */
    yield call(baseClient.post, '/tailored_resumes/fetch_score', {
      job_posting_id: jobPosting.id,
      resume_id: resumeId,
    })

    const searchParams = createAutoTailoringUrlParams({
      mode: AutoTailoringMode.fromMemberJB,
      jobCardId: cardId,
      jobPostingId: jobPosting.id,
    })

    const editorUrl = `/resumes/${resumeId}/edit?${searchParams.toString()}`

    navigate(editorUrl)
  } catch (error) {
    ErrorLogger.log(error)
  }
}

function* handleManualTailoringFromJS({
  payload,
}: ReturnType<typeof sagaActions.autoTailoringFromJS>) {
  try {
    let { resumeId, job, cardId } = payload

    resumeId = yield call(createResumeIfMissed, resumeId)

    if (!resumeId) {
      yield put(actions.setModal())
      return
    }

    cardId = yield call(createCardIfMissed, cardId, job)

    const user: UserData = yield select(userSelectors.userData)

    const {
      data: { jobPosting },
    }: AxiosResponse<CreateJobPostingResponse> = yield call(
      baseClient.post,
      '/tailored_resumes/create_job_posting',
      {
        job_posting: {
          description: job.description,
          job_title: job.job_title,
          company_name: job.company,
        },
      },
    )

    yield call(baseClient.post, '/job-postings/update_selected_resume', {
      user_id: user.id,
      resume_id: resumeId,
      job_posting_id: jobPosting.id,
      job_card_id: cardId,
    })

    const {
      data: { averageScore },
    }: AxiosResponse<FetchScoreResponse> = yield call(
      baseClient.post,
      '/tailored_resumes/fetch_score',
      {
        job_posting_id: jobPosting.id,
        resume_id: resumeId,
      },
    )

    yield call(baseClient.post, `/tailored_resumes/sync_resume_with_job_posting`, null, {
      params: {
        resume_id: resumeId,
        job_posting_id: jobPosting.id,
        average_score: averageScore,
      },
    })

    const searchParams = createAutoTailoringUrlParams({
      mode: AutoTailoringMode.fromMemberJB,
      jobCardId: cardId,
      jobPostingId: jobPosting.id,
      autotailoringStep: AutoTailoringFromPublicJobBoardStep.finished,
    })

    const editorUrl = `/resumes/${resumeId}/edit?${searchParams.toString()}`

    navigate(editorUrl)
  } catch (error) {
    ErrorLogger.log(error)
  }
}

export const sagas = function* saga() {
  yield all([
    takeLatest(sagaActions.fetchJobSearchBubbleFeatureFlag, fetchJobSearchBubbleFeatureFlag),
    takeLatest(sagaActions.disableAutoApplyBubble, disableAutoApplyBubbleSaga),
    takeLatest(sagaActions.startAutoApplyBubble, startAutoApplyBubbleSaga),
    takeLatest(sagaActions.handleNotificationAddedJobCard, handleNotificationAddedJobCard),
    takeLatest(sagaActions.handleNotificationRemovedJobCard, handleNotificationRemovedJobCard),
    takeLatest(sagaActions.autoTailoringFromJS, handleAutoTailoringFromJS),
    takeLatest(sagaActions.manualTailoringFromJS, handleManualTailoringFromJS),
    takeLatest(actions.setAutoApplyBubble, setAutoApplyBubbleSaga),
  ])
}
